/**
 * @fileoverview
 * Tabs Component - Tab navigation component
 * Migrated to new architecture with OnPush change detection
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, inject } from '@angular/core';

import { Router, RouterModule } from '@angular/router';

export interface Tab {
  label: string;
  /** Router path for route-based tabs */
  route?: string;
  exact?: boolean; // Optional: exact route matching (default: false)
  /** Stable id for click-based (non-router) tabs */
  value?: string;
}

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.css',
})
export class TabsComponent {
  private router = inject(Router);

  @Input() tabs: Tab[] = [];

  /**
   * When set, tabs are click-based (no navigation). Parent controls selection via this value.
   */
  @Input() activeValue: string | null = null;

  @Output() tabSelect = new EventEmitter<string>();

  /** Route mode when activeValue is not bound and every tab has a route. */
  get isRouterMode(): boolean {
    return this.activeValue == null && this.tabs.length > 0 && this.tabs.every((t) => !!t.route);
  }

  getTabValue(tab: Tab): string {
    return tab.value ?? tab.route ?? tab.label;
  }

  /**
   * Check if tab is active
   * Uses activeValue for click-based tabs; otherwise route matching
   */
  isActive(tab: Tab): boolean {
    if (!this.isRouterMode) {
      return this.getTabValue(tab) === this.activeValue;
    }
    if (!tab.route) {
      return false;
    }
    if (tab.exact) {
      return this.router.url === tab.route;
    }
    return this.router.url.includes(tab.route);
  }

  /**
   * Get router link active options for a tab
   */
  getRouteLinkOptions(tab: Tab): { exact: boolean } {
    return { exact: tab.exact !== undefined ? tab.exact : false };
  }

  onTabSelect(tab: Tab): void {
    this.tabSelect.emit(this.getTabValue(tab));
  }
}
